import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, keys } from "@shared/api";
import { day, fullDay, plural, sameDay, time } from "@shared/lib/format";
import { jumpToMessage } from "@shared/lib/jump";
import { useStore } from "@features/chat";
import { useMe } from "@entities/session";
import { ReminderDialog } from "@features/message-reminder";
import { CallRow, MessageRow, PendingRow } from "./MessageRow";
import { useInteractions } from "../model/interactions";
import type { CustomEmoji } from "@shared/lib/emoji";
import type { Channel, Message, Named, Role, Uuid } from "@shared/api";
import { IconBan, IconChat, IconDown, IconEdit, IconTextChannel } from "@shared/ui/icons";
import { Spinner } from "@shared/ui/spinner";
import type { MessageLink } from "@shared/lib/jump";
import { overlaid } from "@shared/ui/hotkey";

/**
 * Запоминает верхнюю видимую строку и возвращает способ вернуться к ней.
 *
 * Держаться за высоту здесь нельзя: она меняется с обеих сторон разом —
 * снизу дописали страницу, сверху отрезали столько же. А строка под глазами
 * своё место знает точно, и после перестройки её достаточно найти заново.
 *
 * Не нашлась — значит отрезали как раз её; тогда лучше не трогать прокрутку
 * вовсе, чем прыгать наугад.
 */
function hold(element: HTMLElement): () => void {
  const edge = element.getBoundingClientRect().top;
  const rows = element.querySelectorAll<HTMLElement>("[data-message]");

  const first = [...rows].find((row) => row.getBoundingClientRect().bottom > edge);
  if (!first) return () => {};

  const id = first.dataset.message;
  const was = first.getBoundingClientRect().top;

  return () => {
    const again = element.querySelector<HTMLElement>(`[data-message="${id}"]`);
    if (!again) return;

    element.scrollTop += again.getBoundingClientRect().top - was;
  };
}

/** Сообщения одного человека подряд и в пределах пяти минут — одна серия. */
const SERIES_GAP_MS = 5 * 60 * 1000;
/** Сколько сохранённых держит сервер: сверх этого пункт «Сохранить» не показывается. */
const SAVED_MAX = 100;

function sameMasquerade(a: Message["masquerade"], b: Message["masquerade"]): boolean {
  return (a?.name ?? null) === (b?.name ?? null) && (a?.avatar_url ?? null) === (b?.avatar_url ?? null);
}

function startsSeries(message: Message, previous: Message | undefined): boolean {
  if (!previous) return true;
  // Системное — отдельная строка: ни к нему, ни от него серия не тянется.
  if (previous.kind || message.kind) return true;
  if (previous.author.id !== message.author.id) return true;
  // Вебхук под разными именами — разные авторы, как в Discord.
  if (!sameMasquerade(previous.masquerade, message.masquerade)) return true;
  if (!sameDay(previous.created, message.created)) return true;
  return new Date(message.created).getTime() - new Date(previous.created).getTime() > SERIES_GAP_MS;
}

export function Feed({
  channelId,
  canDeleteOthers,
  canReact,
  canSend,
  canPin,
  announcement = false,
  people,
  custom,
  channels,
  roles,
  myRoles,
  onOpenProfile,
  onOpenChannel,
  editLast = 0,
  onEditDone,
  serverId = null,
  openAt = null,
  onOpenMessage,
  onOpenServer,
  colorOf,
  iconOf,
  nickOf,
  intro,
  threads = true,
}: {
  channelId: Uuid;
  canDeleteOthers: boolean;
  canReact: boolean;
  canSend: boolean;
  /** Закреплять: на сервере — по праву управлять сообщениями, в личных — обоим. */
  canPin: boolean;
  /** Канал объявлений: своё (или с правом управлять сообщениями) можно разослать подписчикам. */
  announcement?: boolean;
  /** Кого знаем по имени. Из них и собирается имя по хэндлу. */
  people: Map<Uuid, Named>;
  /** Знаки этого сервера. В личной переписке их нет. */
  custom: CustomEmoji[];
  /**
   * Каналы этого сервера: их называют через «#» и на них же нажимают.
   *
   * В личной переписке список пуст — называть там нечего, и подсказка по
   * решётке не откроется.
   */
  channels: Channel[];
  /**
   * Роли этого сервера, кроме роли по умолчанию: их называют через «@».
   *
   * В личной переписке список пуст — ролей там нет вовсе.
   */
  roles: Role[];
  /** Свои роли здесь: по ним видно, что через роль позвали именно нас. */
  myRoles: Uuid[];
  onOpenProfile: (userId: Uuid, anchor?: HTMLElement) => void;
  onOpenChannel: (channelId: Uuid) => void;
  /** Сменился — открыть правку своего последнего сообщения (↑ в поле ввода). */
  editLast?: number;
  /** Правка кончилась: фокус — обратно в поле ввода. */
  onEditDone?: () => void;
  /** Чей канал — для ссылок на сообщения. `null` — личная переписка. */
  serverId?: Uuid | null;
  /** Открыться не с конца, а вокруг этого сообщения: сюда привела ссылка. */
  openAt?: Uuid | null;
  /** Перейти к сообщению по ссылке из текста. */
  onOpenMessage?: (link: MessageLink) => void;
  /** Открыть сервер (и канал) — после вступления по карточке приглашения. */
  onOpenServer?: (serverId: Uuid, channelId: Uuid | null) => void;
  /** Цвет имени автора — старшей цветной роли. */
  colorOf?: (userId: Uuid) => string | null;
  /** Значок старшей роли со значком — после имени автора. */
  iconOf?: (userId: Uuid) => string | null;
  /** Ник автора на сервере; `null` — общее имя. */
  nickOf?: (userId: Uuid) => string | null;
  /**
   * Приветствие в начале истории канала, как в Discord: «Добро пожаловать…».
   * Нет — у переписки, там прежняя строка о пустоте.
   */
  intro?: FeedIntro;
  /** Заводить ветки: в личных сообщениях их нет, как в Discord. */
  threads?: boolean;
}) {
  const me = useMe();
  const store = useStore();
  const queryClient = useQueryClient();
  /*
   * Сохранённые — один список на сеанс: меню строки только спрашивает, есть ли
   * сообщение в нём. Свежим его делают «Входящие → Сохранённые» и свои правки.
   */
  const { data: saved } = useQuery({ queryKey: keys.saved(), queryFn: () => api.users.saved(), staleTime: Infinity });
  const savedIds = useMemo(() => new Set((saved ?? []).map((item) => item.message.id)), [saved]);
  const toggleSaved = async (messageId: Uuid) => {
    await (savedIds.has(messageId) ? api.users.unsave(messageId) : api.users.save(messageId)).catch(() => {});
    await queryClient.invalidateQueries({ queryKey: keys.saved() });
  };

  /*
   * Напоминания — тот же один список на сеанс, что и сохранённые: меню только
   * спрашивает, стоит ли напоминание. «Другое…» открывает окно со своим сроком.
   */
  const { data: reminders } = useQuery({
    queryKey: keys.reminders(),
    queryFn: () => api.users.reminders(),
    staleTime: Infinity,
  });
  const remindedIds = useMemo(() => new Set((reminders ?? []).map((item) => item.message.id)), [reminders]);
  const [timing, setTiming] = useState<Uuid | null>(null);
  const remind = async (messageId: Uuid, at: Date) => {
    try {
      await api.users.remind(messageId, at);
    } finally {
      await queryClient.invalidateQueries({ queryKey: keys.reminders() });
    }
  };
  const forget = async (messageId: Uuid) => {
    await api.users.forget(messageId).catch(() => {});
    await queryClient.invalidateQueries({ queryKey: keys.reminders() });
  };
  /*
   * Убранные «Скрыть» эфемерные ответы в ленте больше не показываются — у себя
   * и до перезагрузки, после неё их нет и так.
   */
  const dismissed = useInteractions((state) => state.dismissed);
  const loaded = store.messages(channelId);
  const messages = useMemo(
    () => (loaded && dismissed.size > 0 ? loaded.filter((item) => !dismissed.has(item.id)) : loaded),
    [loaded, dismissed],
  );
  /** Свои вызовы команд в этом канале, пока бот не ответил. */
  const calls = useInteractions((state) => state.calls);
  const waiting = useMemo(
    () => Object.values(calls).filter((call) => call.channelId === channelId),
    [calls, channelId],
  );

  /*
   * Заблокированные — как в Discord: их сообщения подряд свёрнуты в одну
   * строку «N заблокированных сообщений», по «Показать» разворачиваются.
   * Развёрнутое помним по первому сообщению пачки, до ухода из ленты.
   */
  const blocked = useMemo(
    () => new Set(store.relationships.filter((user) => user.relationship === "blocked").map((user) => user.id)),
    [store.relationships],
  );
  const [shown, setShown] = useState<ReadonlySet<Uuid>>(new Set());
  // Пачки, которые человек свернул сам, — даже если ссылка открыла их сразу.
  const [folded, setFolded] = useState<ReadonlySet<Uuid>>(new Set());
  const hidden = (message: Message) => !message.kind && blocked.has(message.author.id);
  const unfold = (head: Uuid, open: boolean) => {
    setShown((previous) => {
      const next = new Set(previous);
      if (open) next.add(head);
      else next.delete(head);
      return next;
    });
    setFolded((previous) => {
      const next = new Set(previous);
      if (open) next.delete(head);
      else next.add(head);
      return next;
    });
  };

  /*
   * Какую строку открыть на правку по ↑. Номер просьбы едет вместе с
   * сообщением: вторая ↑ по тому же сообщению — тоже просьба, а не повтор.
   */
  const [editing, setEditing] = useState<{ id: Uuid; nonce: number } | null>(null);
  useEffect(() => {
    if (!editLast) return;
    const last = messages?.filter((item) => item.author.id === me.id).at(-1);
    if (!last) return;
    setEditing({ id: last.id, nonce: editLast });
    document.querySelector(`.feed [data-message="${last.id}"]`)?.scrollIntoView({ block: "nearest" });
    // Только на просьбу: лента меняется постоянно, а правку открывают нажатием.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editLast]);
  const pending = store.pending(channelId);

  /*
   * Имя по хэндлу: сначала те, кого мы и так знаем — состав сервера, друзья,
   * собеседники, — потом авторы сообщений в самой ленте.
   *
   * Ходить за незнакомым хэндлом на сервер незачем: упоминание, за которым
   * никого нет, останется обычным текстом, и это честнее, чем подсветить имя,
   * которое никого не позвало.
   */
  const nameOf = useCallback(
    (handle: string) => {
      for (const person of people.values()) {
        if (person.handle === handle) return nickOf?.(person.id) || person.display_name || person.username;
      }

      const author = messages?.find((item) => item.author.handle === handle)?.author;
      return author ? nickOf?.(author.id) || author.display_name || author.username : null;
    },
    [people, messages, nickOf],
  );

  /**
   * Знак сервера по имени.
   *
   * Через карту, а не перебором списка: имя ищется на каждый кусок `:имя:` в
   * каждом сообщении окна, и линейный поиск по полусотне знаков в ленте на
   * двести строк успевает стоить заметно.
   */
  /**
   * Имя канала по номеру.
   *
   * Картой, а не перебором, по той же причине, что и у знаков: номер ищется на
   * каждое упоминание в каждой строке окна.
   *
   * Чего в списке нет — того человек не видит, и упоминание останется текстом:
   * так же ведёт себя неизвестный хэндл.
   */
  const channelOf = useMemo(() => {
    const byId = new Map(channels.map((item) => [item.id, item.name]));
    return (id: string) => byId.get(id) ?? null;
  }, [channels]);

  const roleOf = useMemo(() => {
    const byId = new Map(roles.map((item) => [item.id, item.name]));
    return (id: string) => byId.get(id) ?? null;
  }, [roles]);

  const emojiOf = useMemo(() => {
    const byName = new Map(custom.map((item) => [item.name, item]));
    return (name: string) => byName.get(name) ?? null;
  }, [custom]);

  const scroller = useRef<HTMLDivElement>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  /*
   * Куда возвращаться: к черте, в конец или никуда.
   *
   * Состояние, а не ссылка: от него зависит кнопка, то есть его надо рисовать.
   * Пересчитывается на прокрутке и после каждой перестройки окна — других
   * поводов у него нет.
   */
  const [back, setBack] = useState<"line" | "end" | null>(null);
  const [loadingNewer, setLoadingNewer] = useState(false);
  const atBottom = useRef(true);
  /*
   * Как вернуться туда, где были, после перестройки окна.
   *
   * Держались за высоту, пока окно только росло: сверху дописали — вернули
   * прежнее расстояние до низа. Теперь окно ещё и отрезает противоположный
   * край, и расстояние до низа меняется по делу — поправка по нему уводила бы
   * ленту ровно на отрезанное.
   *
   * Поэтому держимся за строку под глазами: сколько бы ни дописали и ни
   * убрали, своё место она знает точно.
   */
  const stay = useRef<(() => void) | null>(null);
  /** Сама черта «новые сообщения» — к ней открывается канал. */
  const mark = useRef<HTMLDivElement>(null);
  /** Положение черты, на котором уже встали. Второй раз не подкручиваем. */
  const aligned = useRef<string | null>(null);

  const seen = useCallback(
    (messageId: string) => store.saw(channelId, messageId),
    /*
     * В зависимостях само действие, а не склейка сторов: у неё тождество
     * меняется на каждое событие из сокета, и обёртка пересобиралась бы на
     * каждое чужое сообщение — вместе с ней и наблюдатель за видимым.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channelId, store.saw],
  );


  /*
   * Где проходит черта «новые сообщения».
   *
   * Читается здесь, а не ниже по разметке, потому что от неё зависит
   * подкрутка ленты: черта появляется не сразу — сначала приходит история, и
   * только потом сводка непрочитанного, — и без неё в списке зависимостей
   * подкрутка не повторялась бы. Лента так и оставалась внизу, на самых
   * свежих сообщениях, а наблюдатель тут же засчитывал их прочитанными.
   */
  const from = store.newSince(channelId);

  /*
   * Прочитанным считается то, что побывало в видимой части ленты.
   *
   * Не «канал открыт» и не «конец ленты виден»: пролистнуть двести сообщений
   * вниз, не увидев их, — не то же самое, что прочесть, а отметка до конца
   * гасила бы их все разом.
   *
   * Порог нулевой: сообщение засчитывается, едва показавшись краем. Требовать
   * его целиком нельзя — длинное не поместится в окно вовсе, и прочитать его
   * станет невозможно.
   */
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;

    const watch = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = (entry.target as HTMLElement).dataset.message;
          if (id) store.saw(channelId, id);
        }
      },
      { root: element, threshold: 0 },
    );

    for (const row of element.querySelectorAll("[data-message]")) watch.observe(row);

    return () => watch.disconnect();
    /*
     * Черта в зависимостях не для вида: наблюдатель докладывает об изменениях,
     * а не о нынешнем положении дел. Пока граница не замерена, отметка стоит —
     * и всё, что было видно в это время, второй раз уже не всплывёт. Замер
     * границы пересобирает наблюдателя, и видимое пересчитывается заново.
     */
    /*
     * Склейки сторов в зависимостях нет намеренно: её тождество меняется на
     * каждое событие из сокета, и взять её сюда значит пересобирать это на
     * каждое чужое сообщение. Уйдёт вместе со склейкой, когда та распустится.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, messages, from, store.saw]);

  /*
   * Окно канала просится один раз, при появлении ленты.
   *
   * Сброс бухгалтерии («мы внизу», «черта уже отработана») отсюда ушёл: лента
   * заведена с ключом по каналу, и другой канал — это другая лента, со своими
   * ссылками. Прежде это делалось здесь же, вторым проходом после отрисовки.
   */
  useEffect(() => {
    // Привела ссылка на сообщение — окно сразу вокруг него, а не с конца.
    if (openAt) void store.openAt(channelId, openAt);
    else void store.loadMessages(channelId);
    /*
     * Склейки сторов в зависимостях нет намеренно: её тождество меняется на
     * каждое событие из сокета, и взять её сюда значит пересобирать это на
     * каждое чужое сообщение. Уйдёт вместе со склейкой, когда та распустится.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  /**
   * Куда вернуться тому, кто отмотал далеко.
   *
   * Мест, где человек «остановился», два, и они разные: черта «новые
   * сообщения» — пока он до неё не дошёл, конец ленты — когда прошёл. Поэтому
   * важно не только есть ли черта, но и с какой она стороны.
   *
   * Сторону определяет разметка, пока черта в окне, и номера — когда её оттуда
   * уже выбросило: идентификаторы седьмой версии растут со временем, и черта
   * новее всего окна значит «ниже нас».
   *
   * Порог в четыреста пикселей — общий для обоих случаев. У нижнего края окна
   * и так стоит строка «К последним», и кнопка над ней была бы той же
   * надписью дважды; а к черте, до которой полэкрана, не возвращаются кнопкой.
   */
  const aim = useCallback(
    (element: HTMLElement): "line" | "end" | null => {
      if (!messages?.length) return null;
      if (element.scrollHeight - element.scrollTop - element.clientHeight <= 400) return null;

      if (from === undefined) return "end";

      const line = mark.current;
      const below = line
        ? line.getBoundingClientRect().top > element.getBoundingClientRect().bottom
        : from > (messages.at(-1)?.id ?? "");

      return below ? "line" : "end";
    },
    [from, messages],
  );

  /**
   * Перейти к сообщению по цитате.
   *
   * Сперва по разметке: если оно в окне — обычный переход с подсветкой, без
   * единого запроса. Нет в окне — набираем окно вокруг него.
   *
   * Раньше второго случая не было вовсе: переход молча не делал ничего, и это
   * было честно ровно до тех пор, пока окно не начало скользить. Теперь
   * процитированное вылетает из него за десяток страниц подъёма, и «молча
   * ничего» стало ответом на обычную просьбу, а не на редкую.
   */
  async function goTo(messageId: Uuid) {
    // Цель в свёрнутой пачке заблокированного — пачка раскрывается: иначе прыгать некуда.
    const head = blockedRuns(messages ?? [], hidden, from).headOf.get(messageId);
    if (head && !shown.has(head)) {
      unfold(head, true);
      await new Promise((done) => requestAnimationFrame(() => requestAnimationFrame(done)));
    }

    if (document.querySelector(`.feed [data-message="${messageId}"]`)) {
      jumpToMessage(messageId);
      return;
    }

    await store.openAt(channelId, messageId);
  }

  /**
   * Вернуться туда, где остановился.
   *
   * Черта бывает и за пределами окна — оно скользит, и отмотавший далеко вверх
   * давно её выбросил. Тогда набираем окно заново: тем же запросом, что и при
   * открытии канала, то есть вокруг черты. Прыгать к тому, чего в разметке
   * нет, нечем.
   */
  async function goBack() {
    if (back === "end") {
      /*
       * Конец уже в окне — просто доехать до него. `jumpToLatest` в этом
       * случае ничего не делает (окно и так последнее), и кнопка молчала:
       * отмотавший вверх в загруженной ленте вниз ею не возвращался.
       */
      if (store.atLatest(channelId)) {
        const element = scroller.current;
        if (!element) return;
        element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
        // Пока ехали, сверху могла догрузиться история и сдвинуть цель — в конце
        // дожимаем до самого низа.
        const settle = () => {
          element.scrollTop = element.scrollHeight;
        };
        element.addEventListener("scrollend", settle, { once: true });
        window.setTimeout(() => {
          element.removeEventListener("scrollend", settle);
          settle();
        }, 900);
        return;
      }
      await store.jumpToLatest(channelId);
      return;
    }

    await toLine();
  }

  /*
   * Shift+PageUp — к первому непрочитанному, как в Discord. Только когда черта
   * есть; в поле с текстом это выделение страницей вверх — его не трогаем.
   */
  const lineKey = useRef<(() => Promise<void>) | null>(null);
  lineKey.current = from === undefined ? null : toLine;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "PageUp" || !event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
      const field = event.target as HTMLInputElement | HTMLTextAreaElement | null;
      if (!lineKey.current || overlaid(event)) return;
      if ((field?.tagName === "TEXTAREA" || field?.tagName === "INPUT") && field.value) return;
      event.preventDefault();
      void lineKey.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /**
   * К черте «новые сообщения»: первое непрочитанное в окне — доехать до самой
   * черты, нет — набрать окно вокруг неё. Черта бывает нарисована и без него
   * (сверху окна, когда граница выше), и сообщение бывает в свёрнутой пачке
   * заблокированного — поэтому едем к черте, а не к сообщению.
   */
  async function toLine() {
    if (from === undefined) return;
    if (mark.current && messages?.some((item) => item.id === from)) {
      mark.current.scrollIntoView({ block: "start", behavior: "smooth" });
      return;
    }

    await store.loadMessages(channelId);
  }

  /* Перестройка окна не должна дёргать ленту: см. `stay` и `hold`. */
  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) return;

    const nowLatest = store.atLatest(channelId);
    const arrived = store.takeJump(channelId);
    if (arrived) atBottom.current = true;

    /*
     * Окно приехало вокруг названного сообщения — подкручиваемся к нему.
     *
     * Раньше остальной бухгалтерии: та решает, вставать ли на черте и не
     * прилипли ли мы к низу, а здесь человек уже сказал, куда смотреть.
     */
    const asked = store.takeJumpTo(channelId);
    if (asked) {
      atBottom.current = false;
      aligned.current = null;
      // Цель в свёрнутой пачке заблокированного — раскрыть и прыгнуть после отрисовки.
      const head = blockedRuns(messages ?? [], hidden, from).headOf.get(asked);
      if (head && !shown.has(head)) {
        unfold(head, true);
        requestAnimationFrame(() => requestAnimationFrame(() => jumpToMessage(asked)));
      } else jumpToMessage(asked);
      setBack(aim(element));
      return;
    }

    if (stay.current) {
      stay.current();
      stay.current = null;
      return;
    }

    /*
     * Канал с непрочитанным открывается на черте, а не в самом низу.
     *
     * Иначе она оказывается выше видимой части, и продолжать приходится
     * наугад: человек видит конец разговора, а не то место, где он его
     * оставил. Немного места над чертой — чтобы было видно, к чему это ответ.
     *
     * Встать надо не один раз на канал, а один раз на положение черты. При
     * возврате в канал лента мгновение показывает окно с прошлого захода —
     * границы в нём может не быть вовсе, и черта стоит сверху. Подкрутиться по
     * такой и успокоиться значит остаться в начале старого окна, когда свежее
     * уже пришло.
     */
    const line = mark.current;
    const anchored = from !== undefined && messages !== undefined && messages.some((item) => item.id === from);
    const place = `${channelId}|${from ?? ""}|${anchored ? "в окне" : "выше"}`;

    // Прыжок в конец сильнее черты: человек попросил конец, а не своё место.
    if (line && aligned.current !== place && !arrived) {
      aligned.current = place;
      const shift = line.getBoundingClientRect().top - element.getBoundingClientRect().top;
      element.scrollTop = Math.max(0, element.scrollTop + shift - 80);

      /*
       * После подкрутки сообщаем, где оказались, а не «не в конце».
       *
       * Непрочитанного бывает три штуки, и тогда конец ленты виден сразу:
       * прокручивать нечего, события прокрутки не будет, и канал остался бы
       * непрочитанным навсегда.
       */
      atBottom.current =
        element.scrollHeight - element.scrollTop - element.clientHeight < 60 && nowLatest;
      store.setAtEnd(atBottom.current);
      return;
    }

    if (line) aligned.current = place;
    if (atBottom.current) element.scrollTop = element.scrollHeight;
    setBack(aim(element));
    /*
     * Склейки сторов в зависимостях нет намеренно: её тождество меняется на
     * каждое событие из сокета, и взять её сюда значит пересобирать это на
     * каждое чужое сообщение. Уйдёт вместе со склейкой, когда та распустится.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, pending.length, channelId, from]);

  /*
   * Окно, которое не прокручивается, само себя не достроит.
   *
   * Подгрузка вниз висит на прокрутке, а канал открывается на черте — под ней
   * может оказаться меньше строк, чем помещается в ленту. Тогда события
   * прокрутки не будет вовсе, и лента замрёт посреди разговора. Досыпаем, пока
   * не появится, что прокручивать.
   */
  useEffect(() => {
    const element = scroller.current;
    if (!element || loadingNewer) return;
    if (element.scrollHeight > element.clientHeight + 60) return;
    if (store.atLatest(channelId)) return;

    setLoadingNewer(true);
    void store
      .loadNewer(channelId)
      .finally(() => setLoadingNewer(false));
    /*
     * Склейки сторов в зависимостях нет намеренно: её тождество меняется на
     * каждое событие из сокета, и взять её сюда значит пересобирать это на
     * каждое чужое сообщение. Уйдёт вместе со склейкой, когда та распустится.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, channelId, loadingNewer, store.loadNewer, store.atLatest]);

  async function onScroll() {
    const element = scroller.current;
    if (!element) return;

    const bottom = element.scrollHeight - element.scrollTop - element.clientHeight;

    /*
     * «Конец ленты» — это и низ прокрутки, и низ разговора разом. Пока окно не
     * доходит до самого свежего, внизу у него край загруженного, и приходящее
     * туда читать за человека нельзя.
     */
    atBottom.current = bottom < 60 && store.atLatest(channelId);
    store.setAtEnd(atBottom.current);
    setBack(aim(element));

    if (element.scrollTop < 120 && !loadingOlder) {
      setLoadingOlder(true);
      stay.current = hold(element);
      try {
        const grew = await store.loadOlder(channelId);
        if (!grew) stay.current = null;
      } finally {
        setLoadingOlder(false);
      }
    }

    /*
     * Якорь нужен и вниз. Приписанное снизу и правда не двигает того, что
     * выше, — но вместе с ним окно отрезает старое сверху, и вот это двигает
     * всё.
     */
    if (bottom < 400 && !loadingNewer && !store.atLatest(channelId)) {
      setLoadingNewer(true);
      stay.current = hold(element);
      try {
        await store.loadNewer(channelId);
      } finally {
        setLoadingNewer(false);
      }
    }
  }

  if (!messages) {
    return (
      <div className="feed" ref={scroller}>
        <div className="feed-spacer" />
        <div className="feed-edge">
          <Spinner /> читаю историю
        </div>
      </div>
    );
  }

  const rows: React.ReactNode[] = [];
  let previous: Message | undefined;

  /*
   * Черта «новые сообщения»: идёт перед первым непрочитанным.
   *
   * Место замерено в хранилище один раз, при открытии канала, и здесь только
   * рисуется: если считать его на каждую отрисовку, черта уезжала бы вниз с
   * каждым новым сообщением — ровно тогда, когда на неё смотрят.
   */
  const line = (
    <div className="feed-new" key="new" ref={mark}>
      <span className="feed-new-rule" />
      новые сообщения
    </div>
  );

  /*
   * Первого непрочитанного в окне нет — значит граница осталась выше, и черта
   * идёт сверху: всё, что видно, пришло уже после неё. Отметку в этом
   * положении хранилище не двигает, пока человек не долистает до границы.
   */
  const above = from !== undefined && !messages.some((item) => item.id === from);
  const unreadCount = store.unread[channelId] ?? 0;
  // Время первого непрочитанного — если оно в окне; нет — полоса без времени.
  const since = messages.find((item) => item.id === from)?.created ?? null;
  if (above && messages.length > 0) rows.push(line);

  const runs = blockedRuns(messages, hidden, from);
  let open = false;

  for (const message of messages) {
    if (message.id === from) rows.push(line);

    if (!previous || !sameDay(previous.created, message.created)) {
      rows.push(
        <div className="feed-edge feed-divider" key={`day-${message.id}`}>
          <span className="rule" />
          {fullDay(message.created)}
          <span className="rule" />
        </div>,
      );
    }

    const ids = runs.heads.get(message.id);
    if (ids) {
      const head = message.id;
      const count = ids.length;
      // Ссылка привела внутрь пачки — она открыта сразу, пока её не свернули сами.
      const unfolded = shown.has(head) || (!!openAt && ids.includes(openAt) && !folded.has(head));
      open = unfolded;
      rows.push(
        /*
         * Свёрнутая строка несёт номер последнего сообщения пачки: наблюдатель
         * засчитывает видимое по `data-message`, и без него канал, где последним
         * написал заблокированный, оставался бы непрочитанным навсегда.
         */
        <div className="feed-blocked" key={`blocked-${head}`} data-message={unfolded ? undefined : ids.at(-1)}>
          <IconBan size={15} />
          <span>{`${count} ${plural(count, "заблокированное сообщение", "заблокированных сообщения", "заблокированных сообщений")}`}</span>
          <span className="feed-blocked-sep">—</span>
          <button type="button" className="link-btn" onClick={() => unfold(head, !unfolded)}>
            {unfolded ? "Скрыть" : plural(count, "Показать сообщение", "Показать сообщения", "Показать сообщения")}
          </button>
        </div>,
      );
    }
    if (runs.headOf.has(message.id) && !open) {
      previous = message;
      continue;
    }

    rows.push(
      <MessageRow
        key={message.id}
        message={message}
        people={people}
        nameOf={nameOf}
        mine={me.handle}
        onSeen={seen}
        head={startsSeries(message, previous)}
        own={message.author.id === me.id}
        canDeleteOthers={canDeleteOthers}
        canReact={canReact}
        canSend={canSend}
        editNow={editing?.id === message.id ? editing.nonce : 0}
        onEditDone={onEditDone}
        serverId={serverId}
        onOpenMessage={onOpenMessage}
        onOpenServer={onOpenServer}
        authorColor={colorOf?.(message.author.id) ?? null}
        authorIcon={iconOf?.(message.author.id) ?? null}
        authorNick={nickOf?.(message.author.id) ?? null}
        canPin={canPin}
        onPin={() => void store.setPinned(channelId, message.id, !message.pinned)}
        saved={savedIds.has(message.id)}
        onSave={
          message.thread_id || (!savedIds.has(message.id) && savedIds.size >= SAVED_MAX)
            ? undefined
            : () => void toggleSaved(message.id)
        }
        onPublish={
          announcement &&
          canSend &&
          !message.published &&
          // Подписчикам уходит текст: без него публиковать нечего.
          !!message.content.trim() &&
          !message.thread_id &&
          !message.kind &&
          (message.author.id === me.id || canDeleteOthers)
            ? () => void store.publish(channelId, message.id).catch(() => {})
            : undefined
        }
        reminded={remindedIds.has(message.id)}
        onRemind={
          message.thread_id || message.kind
            ? undefined
            : (at) =>
                at
                  ? void remind(message.id, at).catch(() => {})
                  : // После закрытия меню: окно и уходящее меню делили бы фокус.
                    setTimeout(() => setTiming(message.id))
        }
        onForget={() => void forget(message.id)}
        onMarkUnread={message.thread_id ? undefined : () => store.markUnread(channelId, message.id)}
        onVote={message.poll ? (ids) => store.vote(channelId, message.id, ids) : undefined}
        onEndPoll={
          message.poll && (message.author.id === me.id || canDeleteOthers)
            ? () => store.endPoll(channelId, message.id)
            : undefined
        }
        onWave={
          message.kind === "join"
            ? () => {
                try {
                  void store.send(channelId, "👋", [], message.id, true);
                } catch {
                  // Приложение перезапускается — помахать можно будет после.
                }
              }
            : undefined
        }
        onEdit={(content, remove) => store.edit(channelId, message.id, content, remove)}
        onDelete={() => void store.remove(channelId, message.id)}
        onOpenProfile={onOpenProfile}
        onReact={(emoji, mine) => void store.toggleReaction(channelId, message.id, emoji, mine)}
        custom={custom}
        emojiOf={emojiOf}
        channelOf={channelOf}
        roleOf={roleOf}
        myRoles={myRoles}
        onOpenChannel={onOpenChannel}
        onReply={() => store.setReplyTo(channelId, message)}
        onJumpTo={(messageId) => void goTo(messageId)}
        onOpenThread={(threadId) => store.openThread(channelId, message.id, threadId)}
        threads={threads}
        threadOpen={
          store.panel?.kind === "thread" && store.panel.rootId === message.id
        }
        channelId={channelId}
      />,
    );

    previous = message;
  }

  return (
    <div className="feed" ref={scroller} onScroll={() => void onScroll()}>
      <div className="feed-spacer" />

      {loadingOlder && (
        <div className="feed-edge">
          <span className="rule" /> подгружаю выше <span className="rule" />
        </div>
      )}

      {/*
        Непрочитанное — полосой сверху, как в Discord: сколько и с какого
        времени, щелчок ведёт к черте, справа «Отметить как прочитанное».
        Плавающей даты нет, тоже как в Discord: день видно в метке у самого
        сообщения (`posted`).
      */}
      {unreadCount > 0 && from !== undefined && (
        <div className="feed-unread float-surface">
          <button type="button" className="feed-unread-go" onClick={() => void toLine()}>
            {`${unreadCount} ${plural(unreadCount, "новое сообщение", "новых сообщения", "новых сообщений")}`}
            {since && ` с ${sameDay(since, new Date()) ? time(since) : `${day(since)}, ${time(since)}`}`}
          </button>
          <button type="button" className="feed-unread-read" onClick={() => store.markRead(channelId)}>
            Отметить как прочитанное
          </button>
        </div>
      )}

      {intro && (messages.length === 0 || store.atStart(channelId)) && <IntroBlock intro={intro} />}

      {!intro && messages.length === 0 && (
        <div className="feed-edge" style={{ justifyContent: "center" }}>
          Здесь ещё ничего не написано. Первое сообщение задаёт тон.
        </div>
      )}

      {rows}

      {timing && (
        <ReminderDialog onSave={(at) => remind(timing, at)} onClose={() => setTiming(null)} />
      )}

      {pending.map((item) => (
        <PendingRow
          key={item.key}
          content={item.content}
          attachments={item.attachmentIds.length}
          failed={item.failed}
          reason={item.reason}
          onRetry={() => void store.retry(item.key)}
          onDrop={() => store.drop(item.key)}
        />
      ))}

      {waiting.map((call) => (
        <CallRow key={call.id} call={call} onDrop={() => useInteractions.getState().drop(call.id)} />
      ))}

      {/*
        Низ окна — не низ разговора. Канал с непрочитанным открывается на
        черте, и внизу у него край загруженного: молчать об этом нельзя, иначе
        середина переписки выглядит её концом.
      */}
      {messages.length > 0 && !store.atLatest(channelId) && (
        <div className="feed-edge">
          <span className="rule" />
          {loadingNewer ? (
            <>
              <Spinner /> подгружаю ниже
            </>
          ) : (
            <button type="button" className="link-btn" onClick={() => void store.jumpToLatest(channelId)}>
              К последним
            </button>
          )}
          <span className="rule" />
        </div>
      )}

      {/*
        Кнопка возврата липнет к низу ленты, а не висит поверх неё: лента —
        сама себе прокручиваемая область, и всё, что «поверх» в ней, пришлось
        бы вешать на предка. Липкая же строка знает своё место без чужой
        помощи и уезжает вместе с содержимым, когда цель наконец видна.
      */}
      {back && (
        <button type="button" className="feed-back float-surface" onClick={() => void goBack()}>
          <IconDown size={13} />
          {back === "line" ? "К непрочитанному" : "К последним"}
        </button>
      )}
    </div>
  );
}

/** Чем встречает начало истории: имя канала, голосовой ли он, можно ли его настроить. */
export interface FeedIntro {
  name: string;
  /** Чат голосового канала: «на канал Основной», а не «на #основной». */
  voice: boolean;
  /** «Настроить канал» — тем, кто вправе; нет — кнопки нет. */
  onEdit?: () => void;
}

/**
 * Начало канала — как в Discord: значок в круге, «Добро пожаловать…», «Это
 * начало канала…» и, кому можно, «Настроить канал». Стоит над первым
 * сообщением, когда окно дошло до него, и в пустом канале.
 */
function IntroBlock({ intro }: { intro: FeedIntro }) {
  const name = intro.voice ? intro.name : `#${intro.name}`;
  return (
    <div className="feed-intro">
      <span className="feed-intro-mark" aria-hidden>
        {intro.voice ? <IconChat size={30} /> : <IconTextChannel size={30} />}
      </span>
      <h2 className="feed-intro-title">
        Добро пожаловать {intro.voice ? "на канал" : "на"} {name}!
      </h2>
      <p className="feed-intro-text">Это начало канала {name}.</p>
      {intro.onEdit && (
        <button type="button" className="feed-intro-edit" onClick={intro.onEdit}>
          <IconEdit size={14} /> Настроить канал
        </button>
      )}
    </div>
  );
}

/**
 * Пачки сообщений заблокированных подряд: первое сообщение пачки — её номер.
 * Черта «новые сообщения» и смена дня пачку рвут: свёрнутая строка через них
 * не перешагивает.
 */
function blockedRuns(messages: Message[], hidden: (message: Message) => boolean, from: Uuid | undefined) {
  const heads = new Map<Uuid, Uuid[]>();
  const headOf = new Map<Uuid, Uuid>();
  let head: Uuid | null = null;
  let previous: Message | undefined;

  for (const message of messages) {
    const broke = message.id === from || !previous || !sameDay(previous.created, message.created);
    if (!hidden(message)) head = null;
    else {
      if (broke || head === null) {
        head = message.id;
        heads.set(head, []);
      }
      heads.get(head)!.push(message.id);
      headOf.set(message.id, head);
    }
    previous = message;
  }

  return { heads, headOf };
}
